import React, { useState } from 'react';
import { StylesConfig } from 'react-select';
import {
  IFieldFieldsFragment,
  INestedFieldQueryQuery,
  INestedFieldTypesQueryQuery,
  INestedFieldTypesQueryQueryVariables,
} from '../../../../utils/apollo/graphqlSchema';
import { usePanelStateContext } from '../context';
import { useQuery } from '@apollo/client';
import { assertNotNil, IReactSelectOption } from '../../../../utils/types';
import { isNil } from 'lodash';

import Loading from '../panel/Loading';
import { ReactSelectMenu } from '../../../Dropdown/StyledReactSelect';
import { ActionRow, NestedFieldDetailsForm } from '../EntitySelectionPanelUI';
import { PrimaryButton } from '../../../Button';
import { toNestedFieldNode } from '../utils';
import nestedFieldTypesQuery from '../../../../pages/Formulas/apollo/nestedFieldTypesQuery';
import {
  convertToOptions,
  extractQueryOptions,
  findOption,
} from './nestedFieldUtils';

interface IProps {
  entity: IFieldFieldsFragment;
}

const styles: StylesConfig<IReactSelectOption, false, never> = {
  control: (base) => ({
    ...base,
    minWidth: 'auto',
  }),
};

const NestedFieldKeyDetails = ({ entity }: IProps) => {
  assertNotNil(entity.nestedFieldMetadata);
  const { fieldKey: nestedFieldKey, key } = entity.nestedFieldMetadata;
  const { schemaId, tags, selectNodeHandler } = usePanelStateContext();
  const [nestedFieldTypes, setNestedFieldTypes] = useState<
    NonNullable<INestedFieldQueryQuery['nestedField']>['types'] | null
  >(null);

  const [type, setType] = useState<string>(entity.type);

  const queryOptions = extractQueryOptions(tags);
  const { loading } = useQuery<
    INestedFieldTypesQueryQuery,
    INestedFieldTypesQueryQueryVariables
  >(nestedFieldTypesQuery, {
    variables: {
      term: '',
      field: nestedFieldKey,
      schemaId,
      includeValues: [entity.key],
      ...queryOptions,
    },
    onCompleted: (data) => setNestedFieldTypes(data.nestedField?.types ?? null),
  });

  if (isNil(nestedFieldTypes)) {
    return <Loading />;
  } else {
    const typeOptions = convertToOptions(nestedFieldTypes);
    const typeValue = findOption(typeOptions, type);

    return (
      <NestedFieldDetailsForm>
        <ReactSelectMenu
          // this component is inside scrollable container which doesn't
          // allow menu list to overflow outside it. Render the menu list
          // at the root of the document to escape.
          // It also requires adjusting the zIndex of the menu portal
          // be above the Popover via `styles`
          menuPortalTarget={document.body}
          value={typeValue}
          isDisabled={loading}
          options={typeOptions}
          placeholder={'Cast to type'}
          styles={styles}
          isClearable={false}
          onChange={(option) => {
            assertNotNil(option);
            setType(option.value);
          }}
        />
        <ActionRow>
          <PrimaryButton
            type="button"
            onClick={() =>
              selectNodeHandler(
                toNestedFieldNode({
                  fieldKey: nestedFieldKey,
                  key,
                  type,
                }),
              )
            }
            trackContext="entity selection"
            trackAction="nested field key confirm"
          >
            Confirm
          </PrimaryButton>
        </ActionRow>
      </NestedFieldDetailsForm>
    );
  }
};
export default NestedFieldKeyDetails;
